import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { PropostaItem } from '@/types'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  findCatalogPlanForItem,
  getCatalogFingerprint,
  mapApiPlanToCatalog,
  syncProposalItemFromCatalog,
  type CatalogPlan,
} from '@/utils/planCatalog'
import { mergeSuportePricingIntoItem } from '@/utils/planSuportePricing'
import type { AvailableItem } from './proposalStudioConstants'

export function useProposalStudioCatalog(
  setProposalItems: Dispatch<SetStateAction<PropostaItem[]>>,
  setCatalogPlans: Dispatch<SetStateAction<CatalogPlan[]>>,
  setAvailableItems: Dispatch<SetStateAction<AvailableItem[]>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  scopeLayoutSignatureRef: MutableRefObject<string>,
  catalogFingerprintRef: MutableRefObject<string>,
) {
  const loadInFlightRef = useRef<Promise<void> | null>(null)
  const loadErrorToastShownRef = useRef(false)

  const loadCatalog = useCallback(
    async (options?: { silent?: boolean }) => {
      if (loadInFlightRef.current) {
        return loadInFlightRef.current
      }

      const run = async () => {
      try {
        const { data } = await api.get('/plans')
        const rawList = Array.isArray(data.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : []
        const catalog: CatalogPlan[] = []
        for (const row of rawList) {
          try {
            catalog.push(mapApiPlanToCatalog(row as unknown as Record<string, unknown>))
          } catch {
            /* ignora plano com payload inválido */
          }
        }
        const fingerprint = getCatalogFingerprint(catalog)
        const catalogChanged = fingerprint !== catalogFingerprintRef.current
        catalogFingerprintRef.current = fingerprint

        setCatalogPlans(catalog)
        setAvailableItems(
          catalog.map((p: CatalogPlan) => ({
            id: p.id,
            nome: p.nome,
            descricao: p.descricaoPlano,
            valor: p.valor,
            tipo: p.tipo,
            recorrente: p.recorrente,
            config: p.config,
            category: p.category,
          })),
        )

        let syncedCount = 0
        let unmatched = 0
        let scopeReorganized = 0
        setProposalItems((prev) =>
          prev.map((item) => {
            const master = findCatalogPlanForItem(item, catalog)
            if (!master) {
              unmatched += 1
              return item
            }
            syncedCount += 1
            const forceScopeSync = true
            if (forceScopeSync) scopeReorganized += 1
            return mergeSuportePricingIntoItem(
              syncProposalItemFromCatalog(item, master, {
                preserveCommercial: true,
                forceScopeSync,
              }),
              catalog,
            )
          }),
        )

        if (catalogChanged || scopeReorganized > 0) {
          scopeLayoutSignatureRef.current = ''
        }

        if (!options?.silent) {
          if (scopeReorganized > 0) {
            toast.info(
              'Escopo sincronizado com Configuração → Planos — salve para persistir no slide.',
            )
          } else if (syncedCount > 0 && catalogChanged) {
            toast.success(`${syncedCount} plano(s) sincronizado(s) com Configuração → Planos`)
          }
          if (unmatched > 0) {
            toast.error(
              `${unmatched} item(ns) sem vínculo no catálogo — adicione o plano novamente pelo seletor.`,
            )
          }
        }
      } catch (err: unknown) {
        if (!options?.silent && !loadErrorToastShownRef.current) {
          loadErrorToastShownRef.current = true
          toast.error(getFriendlyError(err, 'Erro ao carregar planos'))
        }
      } finally {
        setLoading(false)
        loadInFlightRef.current = null
      }
      }

      loadInFlightRef.current = run()
      return loadInFlightRef.current
    },
    [
      catalogFingerprintRef,
      scopeLayoutSignatureRef,
      setAvailableItems,
      setCatalogPlans,
      setProposalItems,
      setLoading,
    ],
  )

  useEffect(() => {
    void loadCatalog()
  }, [loadCatalog])

  useEffect(() => {
    const onFocus = () => {
      void loadCatalog({ silent: true })
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [loadCatalog])

  return { loadCatalog }
}
